<script lang="ts">
  import type {Video} from '@/types/project';
  import {project, draftStore, subtitles, prompt, preview, step, isMobile, thumbDraft, thumbnail, menu} from '@/store';
  import Swal from 'sweetalert2';
  import {MENU, STEP} from '@/types/common';
  import {onMount, tick} from 'svelte';
  import DraftService from '@/services/drafts';
  import {formatDate, wait} from '@/utils/common';
  import {deleteDraft, restoreDraft} from '@/components/drafts/draft-utils';
  import Descriptions from '@/pages/preview/Descriptions.svelte';
  import Dialog from '@/components/dialog/Dialog.svelte';

  export let selectedVideo: Video | null;
  export let transcriptionLoaded: boolean;

  let openDropdownIndex = -1;
  let isLoadingDrafts = true;
  let drafts: any[] = [];
  let clips: any[] = [];
  let isDescriptionsDialogOpen = false;
  let activeDraftId: any = null;

  async function preRestoreDraft(draft: any) {
    if ($isMobile && draft.currentStep !== STEP.PREVIEW)
      return alert('Please go to the last step on desktop to open this draft on Mobile');
    const video = draft.video;
    await new DraftService().getDraft(draft.id).then(response => {
      restoreDraft(response.data, video);
    });
  }

  async function createNewClip() {
    if (!selectedVideo) return;
    menu.set(MENU.CLIPS);
    project.resetState();
    await project.setVideo(selectedVideo);
    await tick();
    await wait(100); // Adding this wait because there are some instances that the project video is still not available
    draftStore.createNewDraft($project);
    project.set({
      selectedSentences: [],
      template: null,
      compiledVideo: null,
      isNew: true,
      isUpdated: false,
      totalDuration: 0
    });

    subtitles.set({items: {raw: [], grouped: []}});
    prompt.resetState();

    project
      .setVideo(selectedVideo)
      .then(() => {
        step.update(STEP.CHOOSE_TEMPLATE);
      })
      .catch(error => {
        if (error && error.message && error.message !== 'Network Error') return;
        Swal.fire({
          titleText: 'Error',
          text: 'Video failed to load, please try again',
          icon: 'error'
        });
      });
  }

  function showLoading() {
    Swal.fire({
      title: 'Please wait Saving now',
      didOpen: () => Swal.showLoading(),
      allowOutsideClick: () => !Swal.isLoading(),
      allowEscapeKey: () => !Swal.isLoading(),
      allowEnterKey: () => !Swal.isLoading()
    });
  }

  function renameProject(clip: any) {
    openDropdownIndex = -1;

    if (!clip) return;

    Swal.fire({
      title: 'Update name of project',
      input: 'text',
      inputValue: clip?.projectName || '',
      showCancelButton: true,
      confirmButtonText: 'Update'
    }).then(async result => {
      if (!result.isConfirmed || result.value == '') return;
      showLoading();
      let drafftResult = false;
      try {
        drafftResult = await draftStore.renameDraftClip(clip.id, {video: clip.video}, result.value);
      } catch (error) {
        drafftResult = false;
      }

      Swal.fire({
        position: 'top-end',
        icon: !drafftResult ? 'error' : 'success',
        title: !drafftResult ? 'Failed to update project' : 'Project updated',
        showConfirmButton: false,
        timer: 1000,
        toast: true
      });

      if (drafftResult) {
        clip.projectName = result.value;
        clips = clips; // trigger svelte re-render
        drafts = drafts;
      }
    });
  }

  async function duplicateProject(selectedClip: any) {
    openDropdownIndex = -1;
    if (!selectedClip) return;

    showLoading();

    let drafftResult = false;
    try {
      drafftResult = Boolean(await draftStore.duplicateDraft(selectedClip.id));
    } catch (error) {
      drafftResult = false;
    }

    Swal.fire({
      position: 'bottom-end',
      icon: !drafftResult ? 'error' : 'success',
      title: !drafftResult ? 'Failed to duplicate project' : 'Project duplicated',
      showConfirmButton: false,
      timer: 1000,
      toast: true
    });

    if (drafftResult) {
      loadClips();
    }
  }

  async function deleteProject(selectedClip: any) {
    openDropdownIndex = -1;
    try {
      const result = await deleteDraft(selectedClip);
      if (!result) return;

      clips = clips.filter(clip => selectedClip !== clip);
      drafts = drafts.filter(draft => selectedClip !== draft);
    } catch (error) {
      Swal.fire({
        position: 'bottom-end',
        icon: 'error',
        title: 'Failed to delete clip',
        showConfirmButton: false,
        timer: 1000,
        toast: true
      });
    }
  }

  function openDropDown(index: number) {
    if (openDropdownIndex !== -1) window.removeEventListener('click', closeDropdown);
    setTimeout(() => {
      openDropdownIndex = index;
      window.addEventListener('click', closeDropdown);
    });
  }

  function closeDropdown() {
    openDropdownIndex = -1;
    window.removeEventListener('click', closeDropdown);
  }

  const loadClips = async () => {
    if (!selectedVideo) return (isLoadingDrafts = false);

    try {
      const draftService = new DraftService();
      const [requestedDrafts] = await Promise.all([draftService.getVideoDrafts(selectedVideo.id)]);
      if (!Array.isArray(requestedDrafts.data)) throw 'requestedDrafts is not an array.';

      drafts = requestedDrafts.data;
    } catch (error) {
      console.error('video drafts error', error);
    }

    isLoadingDrafts = false;
  };

  function closeDialog() {
    isDescriptionsDialogOpen = false;
    activeDraftId = null;
  }

  function showSocialDescription(id: number) {
    activeDraftId = id;
    isDescriptionsDialogOpen = true;
  }

  onMount(() => {
    loadClips();
    draftStore.resetState();
    project.reset();
    thumbDraft.resetState();
    thumbnail.reset();
    preview.resetState();
  });

  const animationDelay = 500;
</script>

<div class="h-full w-full overflow-auto">
  <div class="flex w-full flex-wrap gap-6 p-6">
    {#if !$isMobile}
      <button
        on:click={() => createNewClip()}
        class="flex w-[calc(33.3%-1rem)] flex-col lg:w-[calc(25%-1.5rem)] {transcriptionLoaded
          ? 'cursor-pointer'
          : 'cursor-not-allowed'}"
        disabled={!transcriptionLoaded}
      >
        <div
          class="flex h-full min-h-[9rem] w-full flex-col items-center justify-center overflow-hidden rounded-xl border border-zinc-200 hover:bg-zinc-100 dark:bg-zinc-800"
        >
          <i class="fa-solid fa-plus text-2xl"></i>
          <h1 class="truncate pt-1 text-sm text-zinc-700 dark:text-zinc-200 md:font-medium">Create New Clip</h1>
        </div>
      </button>
    {/if}
    {#if isLoadingDrafts}
      {#each Array(parseInt((Math.random() * 11 + 3).toFixed(0))) as _, i}
        <div class="w-full lg:w-[calc(25%-1.5rem)]">
          <div
            class="h-auto w-full animate-pulse overflow-hidden rounded-xl bg-zinc-300"
            style="animation-delay: {animationDelay * i}ms;"
          >
            <div class="aspect-video h-full w-full"></div>
          </div>
          <div class="w-full">
            <div
              class="mt-1 animate-pulse rounded-md bg-zinc-300 text-sm md:font-medium"
              style="width: {(Math.random() * 80 + 20).toFixed(0)}%; animation-delay: {animationDelay * i}ms;"
            >
              &nbsp;
            </div>
            <div
              class="mt-1 animate-pulse rounded-md bg-zinc-300 text-xs lg:text-sm"
              style="width: {(Math.random() * 90 + 10).toFixed(0)}%; animation-delay: {animationDelay * i}ms;"
            >
              &nbsp;
            </div>
          </div>
        </div>
      {/each}
    {:else if drafts.length > 0 || clips.length > 0}
      {#each drafts as draft, index}
        {#if draft.video}
          <div class="h-full w-full lg:w-[calc(25%-1.5rem)]">
            <button
              on:click={() => preRestoreDraft(draft)}
              class="relative h-auto w-full overflow-hidden rounded-xl border border-transparent bg-zinc-100 hover:border-rose-500 dark:bg-zinc-700"
            >
              {#if !draft.isProject}
                <div class="absolute z-10 flex h-full w-full items-center justify-center bg-black/50">
                  <h1 class="text-xl font-bold text-white">DRAFT</h1>
                </div>
              {/if}
              <img
                class="aspect-video w-full object-cover {draft.isProject ? '' : 'blur-sm grayscale'}"
                src={selectedVideo?.preview}
                alt="video-thumbnail"
              />
            </button>
            <div class="relative w-full">
              <div class="flex">
                <button on:click={() => preRestoreDraft(draft)} class="overflow-hidden pt-1 text-start">
                  <h1 class="truncate pt-1 text-sm text-zinc-700 dark:text-zinc-200 md:font-medium">
                    {draft.projectName || 'Untitled Project'}
                  </h1>
                  <h2 class="truncate pt-1 text-xs text-zinc-500 lg:text-sm">{formatDate(draft.createdAt)}</h2>
                </button>
                <div class="ml-auto flex items-center px-2">
                  <button class="px-2" on:click={() => openDropDown(openDropdownIndex === index ? -1 : index)}>
                    <i class="fa-solid fa-ellipsis-vertical text-xl"></i>
                  </button>
                </div>
              </div>
              <div
                class="absolute z-20 w-full {openDropdownIndex === index
                  ? ''
                  : 'hidden'} grid rounded-lg border border-zinc-200 bg-white text-sm shadow-md"
              >
                {#if draft.isProject}
                  <button on:click={() => showSocialDescription(draft.id)} class="px-4 py-2 hover:text-rose-500">
                    Social Descriptions
                  </button>
                {/if}
                <button on:click={() => duplicateProject(draft)} class="px-4 py-2 hover:text-rose-500">
                  Duplicate Draft
                </button>
                <button on:click={() => renameProject(draft)} class="px-4 py-2 hover:text-rose-500">
                  Rename Draft
                </button>
                <button on:click={() => deleteProject(draft)} class="px-4 py-2 hover:text-rose-500">
                  Delete Draft
                </button>
              </div>
            </div>
          </div>
        {/if}
      {/each}
    {/if}
  </div>
  <Dialog isDialogOpen={isDescriptionsDialogOpen} useIfShow={true}>
    <div class="pointer-events-none flex h-full w-full items-end justify-center lg:items-center">
      <div
        class="pointer-events-auto flex h-auto w-full min-w-[280px] max-w-[680px] flex-col rounded-2xl border border-zinc-400 bg-white shadow-lg dark:border-zinc-600 dark:bg-zinc-700"
        style="z-index:99;"
      >
        <div class="flex w-full gap-2 p-4">
          <span class="text-2xl font-bold">Downloadables</span>
          <button on:click={closeDialog} class="ml-auto rounded-full px-2 hover:bg-zinc-100">
            <i class="fa-solid fa-xmark p-2 text-lg text-black" aria-hidden="true"></i>
          </button>
        </div>
        {#if activeDraftId != null}
          <Descriptions draftId={activeDraftId} />
        {/if}
      </div>
    </div>
  </Dialog>
</div>
